// タイピング練習のローマ字判定（IME を使わず、キー入力をかなに直して1文字ずつ判定する）。
// 純粋な関数だけで書く。画面（pages/TypingPage.tsx）はこの状態を持って描くだけ。
//
// ・揺れは両方受ける：shi/si、chi/ti、tsu/tu、fu/hu、ji/zi、sha/sya など
// ・「ん」は nn（xn）。次が子音（y・n・母音以外）の時だけ n 1つでもよい。末尾の「ん」は nn
// ・「っ」は次の子音を重ねる（tte）か、xtu/ltu で単独に打つ
// ・正しいキーで進み、誤ったキーでは進まずその文字の誤りとして数える

const BASE: Record<string, string[]> = {
  あ: ["a"], い: ["i", "yi"], う: ["u", "wu", "whu"], え: ["e"], お: ["o"],
  か: ["ka", "ca"], き: ["ki"], く: ["ku", "cu", "qu"], け: ["ke"], こ: ["ko", "co"],
  が: ["ga"], ぎ: ["gi"], ぐ: ["gu"], げ: ["ge"], ご: ["go"],
  さ: ["sa"], し: ["shi", "si", "ci"], す: ["su"], せ: ["se", "ce"], そ: ["so"],
  ざ: ["za"], じ: ["ji", "zi"], ず: ["zu"], ぜ: ["ze"], ぞ: ["zo"],
  た: ["ta"], ち: ["chi", "ti"], つ: ["tsu", "tu"], て: ["te"], と: ["to"],
  だ: ["da"], ぢ: ["di"], づ: ["du"], で: ["de"], ど: ["do"],
  な: ["na"], に: ["ni"], ぬ: ["nu"], ね: ["ne"], の: ["no"],
  は: ["ha"], ひ: ["hi"], ふ: ["fu", "hu"], へ: ["he"], ほ: ["ho"],
  ば: ["ba"], び: ["bi"], ぶ: ["bu"], べ: ["be"], ぼ: ["bo"],
  ぱ: ["pa"], ぴ: ["pi"], ぷ: ["pu"], ぺ: ["pe"], ぽ: ["po"],
  ま: ["ma"], み: ["mi"], む: ["mu"], め: ["me"], も: ["mo"],
  や: ["ya"], ゆ: ["yu"], よ: ["yo"],
  ら: ["ra"], り: ["ri"], る: ["ru"], れ: ["re"], ろ: ["ro"],
  わ: ["wa"], ゐ: ["wi"], ゑ: ["we"], を: ["wo"],
  ゔ: ["vu"],
  ぁ: ["xa", "la"], ぃ: ["xi", "li"], ぅ: ["xu", "lu"], ぇ: ["xe", "le"], ぉ: ["xo", "lo"],
  ゃ: ["xya", "lya"], ゅ: ["xyu", "lyu"], ょ: ["xyo", "lyo"], ゎ: ["xwa", "lwa"],
  ー: ["-"], "、": [","], "。": ["."],
};

// 2文字で1音になるもの（拗音など）。BASE の組み合わせ（ki+xya 等）も別に受ける
const COMBO: Record<string, string[]> = {
  きゃ: ["kya"], きゅ: ["kyu"], きょ: ["kyo"], きぇ: ["kye"],
  ぎゃ: ["gya"], ぎゅ: ["gyu"], ぎょ: ["gyo"],
  しゃ: ["sha", "sya"], しゅ: ["shu", "syu"], しょ: ["sho", "syo"], しぇ: ["she", "sye"],
  じゃ: ["ja", "jya", "zya"], じゅ: ["ju", "jyu", "zyu"], じょ: ["jo", "jyo", "zyo"], じぇ: ["je", "jye", "zye"],
  ちゃ: ["cha", "tya", "cya"], ちゅ: ["chu", "tyu", "cyu"], ちょ: ["cho", "tyo", "cyo"], ちぇ: ["che", "tye", "cye"],
  ぢゃ: ["dya"], ぢゅ: ["dyu"], ぢょ: ["dyo"],
  にゃ: ["nya"], にゅ: ["nyu"], にょ: ["nyo"],
  ひゃ: ["hya"], ひゅ: ["hyu"], ひょ: ["hyo"],
  びゃ: ["bya"], びゅ: ["byu"], びょ: ["byo"],
  ぴゃ: ["pya"], ぴゅ: ["pyu"], ぴょ: ["pyo"],
  みゃ: ["mya"], みゅ: ["myu"], みょ: ["myo"],
  りゃ: ["rya"], りゅ: ["ryu"], りょ: ["ryo"],
  ふぁ: ["fa"], ふぃ: ["fi"], ふぇ: ["fe"], ふぉ: ["fo"],
  てぃ: ["thi"], でぃ: ["dhi"], とぅ: ["twu"], どぅ: ["dwu"],
  うぃ: ["wi"], うぇ: ["we"], うぉ: ["who"],
  ゔぁ: ["va"], ゔぃ: ["vi"], ゔぇ: ["ve"], ゔぉ: ["vo"],
};

const SOKUON_ALONE = ["xtu", "ltu", "xtsu", "ltsu"];
const N_ALONE = ["nn", "xn"];

export type TypingUnit = {
  /** この単位が覆うかな（表示用・元の表記のまま） */
  kana: string;
  /** 受け付けるローマ字 */
  options: string[];
};

function toHiragana(s: string): string {
  return s.replace(/[ァ-ヶ]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0x60));
}

function cartesian(a: string[], b: string[]): string[] {
  const out: string[] = [];
  for (const x of a) for (const y of b) out.push(x + y);
  return out;
}

function uniq(xs: string[]): string[] {
  return [...new Set(xs)];
}

/** 「ん」の後に n 1つで済ませてよい相手か（子音始まり。y・n・母音は不可） */
function allowsSingleN(next: TypingUnit | undefined): boolean {
  if (!next) return false;
  return next.options.every((o) => o.length > 0 && !"aiueoyn".includes(o[0]) && /[a-z]/.test(o[0]));
}

/**
 * 出題のかなを打つ単位に分ける。知らない文字があれば例外を投げる（出題側の誤り）。
 */
export function buildUnits(prompt: string): TypingUnit[] {
  const src = [...prompt];
  const hira = src.map(toHiragana);

  // 1) っ・ん 以外を、拗音をまとめながら単位に分ける。っ・ん は印だけ置く
  type Raw = { kana: string; options: string[] } | { kana: string; mark: "sokuon" | "n" };
  const raws: Raw[] = [];
  for (let i = 0; i < hira.length; i++) {
    const h = hira[i];
    if (h === "っ") { raws.push({ kana: src[i], mark: "sokuon" }); continue; }
    if (h === "ん") { raws.push({ kana: src[i], mark: "n" }); continue; }
    const pair = h + (hira[i + 1] ?? "");
    if (i + 1 < hira.length && COMBO[pair]) {
      const split = cartesian(BASE[h] ?? [], BASE[hira[i + 1]] ?? []);
      raws.push({ kana: src[i] + src[i + 1], options: uniq([...COMBO[pair], ...split]) });
      i++;
      continue;
    }
    const opts = BASE[h];
    if (!opts) throw new Error(`typing: unsupported character "${src[i]}" in "${prompt}"`);
    raws.push({ kana: src[i], options: opts });
  }

  // 2) 後ろから組み立てる（ん・っ は次の単位を見て決まる）
  const units: TypingUnit[] = [];
  for (let i = raws.length - 1; i >= 0; i--) {
    const r = raws[i];
    const next = units[0];
    if ("options" in r) {
      units.unshift({ kana: r.kana, options: r.options });
    } else if (r.mark === "n") {
      units.unshift({ kana: r.kana, options: allowsSingleN(next) ? ["n", ...N_ALONE] : [...N_ALONE] });
    } else {
      // っ：次の単位と一緒にする（次の子音を重ねる）。次が無い・母音等なら単独で打つ
      if (!next) { units.unshift({ kana: r.kana, options: [...SOKUON_ALONE] }); continue; }
      const doubled: string[] = [];
      for (const o of next.options) {
        const c = o[0];
        if (/[a-z]/.test(c) && !"aiueon".includes(c)) doubled.push(c + o);
        if (o.startsWith("ch")) doubled.push("t" + o);
      }
      const alone = cartesian(SOKUON_ALONE, next.options);
      units[0] = { kana: r.kana + next.kana, options: uniq([...doubled, ...alone]) };
    }
  }
  return units;
}

/** 手本に出すローマ字（各単位の最初の候補をつなぐ） */
export function romajiGuide(prompt: string): string {
  return buildUnits(prompt).map((u) => u.options[0]).join("");
}

export type TypingState = {
  prompts: string[];
  units: TypingUnit[][];
  /** 今の問題 */
  promptIndex: number;
  /** 今の問題で打ち終えた単位の数 */
  unitIndex: number;
  /** 今の単位で打った途中のキー */
  buffer: string;
  /** 今の問題で正しく打ったキー（手本の表示用） */
  romaji: string;
  totalKeys: number;
  correctKeys: number;
  done: boolean;
};

export function createTypingState(prompts: string[]): TypingState {
  return {
    prompts,
    units: prompts.map(buildUnits),
    promptIndex: 0,
    unitIndex: 0,
    buffer: "",
    romaji: "",
    totalKeys: 0,
    correctKeys: 0,
    done: prompts.length === 0,
  };
}

/** 今の単位を打ち終えた扱いにして次へ（問題の最後なら次の問題へ、全部終われば done） */
function advance(s: TypingState): TypingState {
  const units = s.units[s.promptIndex];
  const unitIndex = s.unitIndex + 1;
  if (unitIndex < units.length) return { ...s, unitIndex, buffer: "" };
  const promptIndex = s.promptIndex + 1;
  if (promptIndex < s.prompts.length) return { ...s, promptIndex, unitIndex: 0, buffer: "", romaji: "" };
  return { ...s, unitIndex, buffer: "", done: true };
}

/** buffer がこの単位の候補のどれかとちょうど一致し、それより長い候補の頭でもない＝確定 */
function settle(s: TypingState): TypingState {
  const units = s.units[s.promptIndex];
  const unit = units[s.unitIndex];
  if (!unit.options.includes(s.buffer)) return s;
  const isLast = s.unitIndex === units.length - 1;
  const longer = unit.options.some((o) => o.length > s.buffer.length && o.startsWith(s.buffer));
  // 問題の最後の単位は続きを待たずに確定する（次のキーは次の問題のもの）
  if (longer && !isLast) return s;
  return advance(s);
}

/**
 * 1キー打つ。key は1文字（英字・- など）。返り値の correct でそのキーの正誤が分かる。
 */
export function typeKey(state: TypingState, rawKey: string): { state: TypingState; correct: boolean } {
  if (state.done) return { state, correct: false };
  const key = rawKey.toLowerCase();
  const counted = { ...state, totalKeys: state.totalKeys + 1 };
  const unit = counted.units[counted.promptIndex][counted.unitIndex];
  const candidate = counted.buffer + key;

  if (unit.options.some((o) => o.startsWith(candidate))) {
    const next = settle({ ...counted, buffer: candidate, romaji: counted.romaji + key, correctKeys: counted.correctKeys + 1 });
    return { state: next, correct: true };
  }

  // 「ん」の n のように、すでに確定できる buffer があれば確定させて、このキーを次の単位で判定する
  if (counted.buffer !== "" && unit.options.includes(counted.buffer)) {
    const moved = advance({ ...state });
    if (!moved.done) return typeKey(moved, key);
  }

  return { state: counted, correct: false };
}

/** 手本：打ったローマ字と、これから打つ分（今の buffer に合う候補→以降は各単位の最初の候補） */
export function guideParts(s: TypingState): { typed: string; rest: string } {
  if (s.done) return { typed: s.romaji, rest: "" };
  const units = s.units[s.promptIndex];
  const cur = units[s.unitIndex];
  const pick = cur.options.find((o) => o.startsWith(s.buffer)) ?? cur.options[0];
  const rest = pick.slice(s.buffer.length) + units.slice(s.unitIndex + 1).map((u) => u.options[0]).join("");
  return { typed: s.romaji, rest };
}

/** 今の問題で打ち終えたかな */
export function typedKana(s: TypingState): string {
  if (s.done) return s.prompts[s.prompts.length - 1] ?? "";
  return s.units[s.promptIndex].slice(0, s.unitIndex).map((u) => u.kana).join("");
}

/** 今の問題でまだ打っていないかなの数 */
export function remainingKana(s: TypingState): number {
  if (s.done) return 0;
  const prompt = s.prompts[s.promptIndex];
  return [...prompt].length - [...typedKana(s)].length;
}

/** 正確さ（四捨五入の整数 %）。1キーも打っていなければ 0 */
export function accuracyPercent(totalKeys: number, correctKeys: number): number {
  if (totalKeys <= 0) return 0;
  return Math.round((correctKeys / totalKeys) * 100);
}

/** 1分あたりの文字数（かなの数で数える） */
export function charsPerMinute(prompts: string[], durationMs: number): number {
  const chars = prompts.reduce((n, p) => n + [...p].length, 0);
  if (durationMs <= 0) return 0;
  return Math.round(chars / (durationMs / 60000));
}

// 結果 API が受ける上限（1回の問題数・1問の長さ）
const MAX_PROMPTS = 50;
const MAX_PROMPT_CHARS = 40;

/** q の値（カンマ区切り）を出題に直す。打てない文字を含む問題・長すぎる問題は捨てる */
export function parsePromptQuery(q: string | null): string[] | null {
  if (q == null) return null;
  const items = q.split(/[,、，]/).map((s) => s.trim()).filter((s) => s !== "");
  const ok = items.filter((p) => {
    if ([...p].length > MAX_PROMPT_CHARS) return false;
    try { return buildUnits(p).length > 0; } catch { return false; }
  });
  return ok.length > 0 ? ok.slice(0, MAX_PROMPTS) : null;
}
